import { setSheetTheOnlyVisible } from "@/store/selections-slice";
import type { RootState } from "@/store/store";
import {
  isIElementGenericImgSheet,
  type GUID,
} from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import type { TrackedState } from "./tracked-state";

/**
 * The active sheet (floor). Unlike the other parts it is not part of a saved view
 * yet, see SWEB-8956.
 */
export type ActiveSheetEntry = {
  /** The sheet used for the 2D navigation, absent in areas without sheets */
  sheetId?: GUID;
};

/**
 * @param state The current state
 * @returns the first active sheet, the one used for the 2D navigation
 */
function selectActiveSheetId(state: RootState): GUID | undefined {
  return state.selections.activeSheets.at(0);
}

/**
 * Tracks the sheet the user picked in the sheet menu. Restoring only makes it the
 * visible sheet. The active sheets follow once the mode data updates, from the
 * visible sheets and the area of the element restored by `elementAndMode`, so
 * the order of the two restores does not matter.
 *
 * The sheets overlaid with the checkboxes are a view setting and not tracked:
 * toggling one only adds an entry if it changes the first active sheet.
 */
export const activeSheet: TrackedState<ActiveSheetEntry> = {
  /**
   * @param state The current state
   * @returns the active sheet, or an empty part without one
   */
  capture(state) {
    const sheetId = selectActiveSheetId(state);
    return sheetId ? { sheetId } : {};
  },

  /** @returns whether the value has no sheet or a sheet id */
  isEntry({ sheetId }) {
    return sheetId === undefined || typeof sheetId === "string";
  },

  /**
   * @param state The current state
   * @returns whether the sheet, if any, still exists and is a sheet
   */
  canRestore({ sheetId }, state) {
    return (
      sheetId === undefined ||
      isIElementGenericImgSheet(selectIElement(sheetId)(state))
    );
  },

  /**
   * Picks the sheet the same way the sheet menu does. An already active sheet is
   * left alone, keeping the overlaid sheets.
   *
   * @returns a thunk restoring the active sheet, if the entry has one
   */
  restore({ sheetId }) {
    return (dispatch, getState) => {
      if (!sheetId || sheetId === selectActiveSheetId(getState())) return;
      dispatch(setSheetTheOnlyVisible(sheetId));
    };
  },
};
